import { QueryStatus, useQuery } from "@tanstack/react-query";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";

import { message, cn } from "@/lib/utils";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import { LockedContent } from "@/components/layout/locked-content";

type ConsumerThemeRow = {
  rank: number;
  name: string;
  frequency: number;
  isPositive: boolean;
};

function TopConsumerThemesBody({ status, consumerThemes }: { status: QueryStatus; consumerThemes: ConsumerThemeRow[] }) {
  switch (status) {
    case "pending":
      return <TopConsumerThemesSkeleton />;
    case "error":
      return <LockedContent message={message} className="min-h-64" />;
    case "success":
      return consumerThemes.length ? <TopConsumerThemesList themes={consumerThemes} /> : <LockedContent message={message} className="min-h-64" />;
  }
}

export function TopConsumerThemes() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.OverviewTopConsumerThemes());

  const consumerThemes: ConsumerThemeRow[] = !data
    ? []
    : [
        ...data.analyzed_positive_themes.map((theme) => ({
          name: theme.theme,
          frequency: theme.frequency,
          isPositive: true,
        })),
        ...data.analyzed_negative_themes.map((theme) => ({
          name: theme.theme,
          frequency: theme.frequency,
          isPositive: false,
        })),
      ]
        .sort((a, b) => b.frequency - a.frequency)
        .slice(0, 8)
        .map((theme, index) => ({ ...theme, rank: index + 1 }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Consumer Themes</CardTitle>
      </CardHeader>
      <CardContent>
        <TopConsumerThemesBody status={status} consumerThemes={consumerThemes} />
      </CardContent>
    </Card>
  );
}

function TopConsumerThemesList({ themes }: { themes: ConsumerThemeRow[] }) {
  const maxValue = Math.max(...themes.map((theme) => theme.frequency), 0);

  return (
    <div className="space-y-6">
      {themes.map((theme) => {
        const progressValue = maxValue > 0 ? (theme.frequency / maxValue) * 100 : 0;
        const frequencyText = `${theme.frequency}%`;

        return (
          <div key={theme.rank} className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">{theme.rank}</span>
                <p className="text-sm font-medium text-foreground truncate">{theme.name}</p>
              </div>
              <span className={cn("text-sm font-medium", theme.isPositive ? "text-success" : "text-destructive")}>{frequencyText}</span>
            </div>
            <div className="flex items-center gap-2 pl-4">
              <Progress value={progressValue} className="h-1 flex-1 bg-muted" indicatorClassName={cn("rounded-full", theme.isPositive ? "bg-success" : "bg-destructive")} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TopConsumerThemesSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 5 }, (_, index) => (
        <div key={index} className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4 rounded-md" />
              <Skeleton className="h-4 w-32 rounded-md" />
            </div>
            <Skeleton className="h-4 w-12 rounded-md" />
          </div>
          <div className="pl-4">
            <Skeleton className="h-1 w-full rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
